import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Label,
  Badge,
  Spinner,
} from "reactstrap";

import CountUp from "react-countup";

import api from "../../../utills/api";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";


const INITIAL_FORM = {
  channelManager: "BOOKINGCOM",
  pmsRoomTypeId: "",
  pmsRatePlanName: "BAR",
  channelRoomCode: "",
  channelRateCode: "",
};


const CHANNEL_OPTIONS = [
  {
    value: "BOOKINGCOM",
    label: "Booking.com",
  },
  {
    value: "EXPEDIA",
    label: "Expedia",
  },
  {
    value: "AIRBNB",
    label: "Airbnb",
  },
  {
    value: "AGODA",
    label: "Agoda",
  },
];


const RoomMapping = () => {
  document.title =
    "HMS | Channel Room Mapping";


  /* =====================================================
     STATE
  ===================================================== */

  const [roomTypes, setRoomTypes] =
    useState([]);

  const [mappings, setMappings] =
    useState([]);

  const [mappingLogs, setMappingLogs] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [initialLoading, setInitialLoading] =
    useState(true);

  const [logsLoading, setLogsLoading] =
    useState(false);

  const [editingId, setEditingId] =
    useState(null);

  const [form, setForm] =
    useState(INITIAL_FORM);


  /* =====================================================
     LOAD ROOM TYPES
  ===================================================== */

  const loadRoomTypes = async () => {
    const res =
      await api.get(
        "/manage/room-types"
      );

    const data =
      Array.isArray(res.data)
        ? res.data
        : res.data?.roomTypes || [];

    setRoomTypes(data);

    return data;
  };


  /* =====================================================
     LOAD MAPPINGS
  ===================================================== */

  const loadMappings = async () => {
    const res =
      await api.get(
        "/room-mappings"
      );

    const data =
      Array.isArray(
        res.data?.mappings
      )
        ? res.data.mappings
        : [];

    setMappings(data);

    return data;
  };


  /* =====================================================
     LOAD LOGS
  ===================================================== */

  const loadLogs = async () => {
    try {
      setLogsLoading(true);

      const res =
        await api.get(
          "/room-mappings/logs"
        );

      /*
       * Supports both:
       *
       * { success: true, logs: [] }
       *
       * and current backend:
       *
       * []
       */

      const logs =
        Array.isArray(res.data)
          ? res.data
          : res.data?.logs || [];

      setMappingLogs(logs);

    } catch (err) {

      console.error(
        "Failed to load mapping logs:",
        err
      );

      setMappingLogs([]);

    } finally {

      setLogsLoading(false);

    }
  };


  /* =====================================================
     INITIAL LOAD
  ===================================================== */

  useEffect(() => {

    const loadData = async () => {

      try {

        setInitialLoading(true);

        await Promise.all([
          loadRoomTypes(),
          loadMappings(),
          loadLogs(),
        ]);

      } catch (err) {

        console.error(
          "Room mapping load error:",
          err
        );

        alert(
          err.response?.data?.message ||
          "Failed to load room mapping data"
        );

      } finally {

        setInitialLoading(false);

      }

    };

    loadData();

  }, []);


  /* =====================================================
     FORM CHANGE
  ===================================================== */

  const handleChange = (e) => {

    const {
      name,
      value,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

  };


  /* =====================================================
     SELECTED ROOM TYPE
  ===================================================== */

  const selectedRoomType =
    useMemo(() => {

      return roomTypes.find(
        (room) =>
          String(room._id) ===
          String(form.pmsRoomTypeId)
      );

    }, [
      roomTypes,
      form.pmsRoomTypeId,
    ]);


  /* =====================================================
     ROOM TYPE NAME
  ===================================================== */

  const getRoomTypeName = (
    room
  ) => {

    return (
      room?.name ||
      room?.roomTypeName ||
      room?.roomType ||
      "Unknown Room Type"
    );

  };


  /* =====================================================
     RESET FORM
  ===================================================== */

  const resetForm = () => {

    setForm({
      ...INITIAL_FORM,
    });

    setEditingId(null);

  };


  /* =====================================================
     VALIDATION
  ===================================================== */

  const validateForm = () => {

    if (!form.channelManager) {
      alert(
        "Please select channel manager"
      );

      return false;
    }

    if (!form.pmsRoomTypeId) {
      alert(
        "Please select PMS room type"
      );

      return false;
    }

    if (!form.pmsRatePlanName.trim()) {
      alert(
        "Please enter rate plan"
      );

      return false;
    }

    if (!form.channelRoomCode.trim()) {
      alert(
        "Please enter channel room code"
      );

      return false;
    }

    if (!form.channelRateCode.trim()) {
      alert(
        "Please enter channel rate code"
      );

      return false;
    }

    return true;

  };


  /* =====================================================
     SAVE / UPDATE MAPPING
  ===================================================== */

  const handleSave = async () => {

    if (!validateForm()) {
      return;
    }

    try {

      setLoading(true);

      const roomTypeName =
        getRoomTypeName(
          selectedRoomType
        );

      const payload = {
        channelManager:
          form.channelManager,

        pmsRoomTypeId:
          form.pmsRoomTypeId,

        pmsRoomTypeName:
          roomTypeName,

        pmsRatePlanName:
          form.pmsRatePlanName.trim() ||
          "BAR",

        channelRoomCode:
          form.channelRoomCode.trim(),

        channelRateCode:
          form.channelRateCode.trim(),
      };


      console.log(
        "ROOM MAPPING PAYLOAD:",
        payload
      );


      await api.post(
        "/room-mappings",
        payload
      );


      await Promise.all([
        loadMappings(),
        loadLogs(),
      ]);


      alert(
        editingId
          ? "Mapping updated successfully"
          : "Mapping saved successfully"
      );


      resetForm();

    } catch (err) {

      console.error(
        "Save mapping error:",
        err
      );

      alert(
        err.response?.data?.message ||
        "Failed to save room mapping"
      );

    } finally {

      setLoading(false);

    }

  };


  /* =====================================================
     EDIT MAPPING
  ===================================================== */

  const handleEdit = (mapping) => {

    setEditingId(
      mapping._id
    );

    setForm({
      channelManager:
        mapping.channelManager ||
        "BOOKINGCOM",

      pmsRoomTypeId:
        mapping.pmsRoomTypeId?._id ||
        mapping.pmsRoomTypeId ||
        "",

      pmsRatePlanName:
        mapping.pmsRatePlanName ||
        "BAR",

      channelRoomCode:
        mapping.channelRoomCode ||
        "",

      channelRateCode:
        mapping.channelRateCode ||
        "",
    });


    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  };


  /* =====================================================
     DELETE
     
     IMPORTANT:
     Your current backend does NOT provide:
     
     DELETE /room-mappings/:id
     
     Therefore this frontend does not call DELETE.
  ===================================================== */

  const handleDelete = () => {

    alert(
      "Delete is not available yet. Add a DELETE endpoint in roomMappingRoutes.js before enabling this action."
    );

  };


  /* =====================================================
     WIDGETS
  ===================================================== */

  const widgets = useMemo(() => {

    const total =
      mappings.length;

    const active =
      mappings.filter(
        (m) =>
          m.status === "Active"
      ).length;

    const synced =
      mappings.filter(
        (m) =>
          m.syncStatus === "Synced"
      ).length;

    const channels =
      new Set(
        mappings
          .map(
            (m) =>
              m.channelManager
          )
          .filter(Boolean)
      ).size;


    return [
      {
        label:
          "Total Mappings",

        value:
          total,

        icon:
          "bx bx-link",

        color:
          "#0d6efd",

        bgColor:
          "rgba(13,110,253,.12)",
      },

      {
        label:
          "Active Mappings",

        value:
          active,

        icon:
          "bx bx-check-circle",

        color:
          "#198754",

        bgColor:
          "rgba(25,135,84,.12)",
      },

      {
        label:
          "Synced",

        value:
          synced,

        icon:
          "bx bx-sync",

        color:
          "#20c997",

        bgColor:
          "rgba(32,201,151,.12)",
      },

      {
        label:
          "Channels",

        value:
          channels,

        icon:
          "bx bx-globe",

        color:
          "#6f42c1",

        bgColor:
          "rgba(111,66,193,.12)",
      },
    ];

  }, [mappings]);


  /* =====================================================
     MAPPING TABLE
  ===================================================== */

  const mappingColumns =
    useMemo(
      () => [

        {
          header:
            "Channel",

          accessorKey:
            "channelManager",

          cell: ({
            getValue,
          }) => {

            const value =
              getValue();

            const label =
              CHANNEL_OPTIONS.find(
                (item) =>
                  item.value ===
                  value
              )?.label ||
              value ||
              "-";

            return (
              <Badge color="primary">
                {label}
              </Badge>
            );

          },
        },


        {
          header:
            "Room Type",

          accessorKey:
            "pmsRoomTypeName",

          cell: ({
            row,
          }) => {

            const mapping =
              row.original;

            return (
              <div>
                <div className="fw-semibold">
                  {
                    mapping.pmsRoomTypeName ||
                    "-"
                  }
                </div>

                <small className="text-muted">
                  ID:{" "}
                  {
                    mapping.pmsRoomTypeId?._id ||
                    mapping.pmsRoomTypeId ||
                    "-"
                  }
                </small>
              </div>
            );

          },
        },


        {
          header:
            "Rate Plan",

          accessorKey:
            "pmsRatePlanName",

          cell: ({
            getValue,
          }) => (
            <Badge color="secondary">
              {getValue() || "BAR"}
            </Badge>
          ),
        },


        {
          header:
            "Channel Room Code",

          accessorKey:
            "channelRoomCode",

          cell: ({
            getValue,
          }) => (
            <span className="fw-semibold">
              {getValue() || "-"}
            </span>
          ),
        },


        {
          header:
            "Channel Rate Code",

          accessorKey:
            "channelRateCode",

          cell: ({
            getValue,
          }) => (
            <span className="fw-semibold">
              {getValue() || "-"}
            </span>
          ),
        },


        {
          header:
            "Status",

          accessorKey:
            "status",

          cell: ({
            getValue,
          }) => {

            const active =
              getValue() ===
              "Active";

            return (
              <Badge
                color={
                  active
                    ? "success"
                    : "danger"
                }
              >
                {active
                  ? "Active"
                  : "Inactive"}
              </Badge>
            );

          },
        },


        {
          header:
            "Sync",

          accessorKey:
            "syncStatus",

          cell: ({
            getValue,
          }) => {

            const synced =
              getValue() ===
              "Synced";

            return (
              <Badge
                color={
                  synced
                    ? "success"
                    : "warning"
                }
              >
                {getValue() || "Pending"}
              </Badge>
            );

          },
        },


        {
          header:
            "Action",

          id:
            "action",

          cell: ({
            row,
          }) => {

            const mapping =
              row.original;

            return (
              <div className="d-flex gap-2">

                <Button
                  size="sm"
                  color="primary"
                  outline
                  onClick={() =>
                    handleEdit(
                      mapping
                    )
                  }
                >
                  <i className="bx bx-edit" />
                </Button>

                <Button
                  size="sm"
                  color="danger"
                  outline
                  onClick={() =>
                    handleDelete(
                      mapping._id
                    )
                  }
                >
                  <i className="bx bx-trash" />
                </Button>

              </div>
            );

          },
        },

      ],
      [
        roomTypes,
        mappings,
      ]
    );


  /* =====================================================
     LOG TABLE
  ===================================================== */

  const logColumns =
    useMemo(
      () => [

        {
          header:
            "Date",

          accessorKey:
            "createdAt",

          cell: ({
            getValue,
          }) => {

            const value =
              getValue();

            if (!value) {
              return "-";
            }

            return new Date(
              value
            ).toLocaleString(
              "en-IN"
            );

          },
        },


        {
          header:
            "Room Type",

          accessorKey:
            "roomTypeName",

          cell: ({
            getValue,
          }) =>
            getValue() || "-",
        },


        {
          header:
            "Channel",

          accessorKey:
            "channelManager",

          cell: ({
            getValue,
          }) => {

            const value =
              getValue();

            return (
              CHANNEL_OPTIONS.find(
                (item) =>
                  item.value ===
                  value
              )?.label ||
              value ||
              "-"
            );

          },
        },


        {
          header:
            "Action",

          accessorKey:
            "action",

          cell: ({
            getValue,
          }) => (
            <Badge color="info">
              {getValue() || "-"}
            </Badge>
          ),
        },


        {
          header:
            "Updated By",

          accessorKey:
            "updatedBy",

          cell: ({
            getValue,
          }) =>
            getValue() || "Admin",
        },

      ],
      []
    );


  /* =====================================================
     LOADING SCREEN
  ===================================================== */

  if (initialLoading) {

    return (
      <div className="page-content">

        <Container fluid>

          <BreadCrumb
            title="Channel Manager"
            pageTitle="Room Mapping"
          />

          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minHeight:
                "300px",
            }}
          >
            <div className="text-center">

              <Spinner
                color="primary"
              />

              <div className="mt-2 text-muted">
                Loading room mappings...
              </div>

            </div>
          </div>

        </Container>

      </div>
    );

  }


  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="page-content">

      <Container fluid>

        <BreadCrumb
          title="Channel Manager"
          pageTitle="Room Mapping"
        />


        {/* =================================================
            WIDGETS
        ================================================= */}

        <Row>

          {widgets.map(
            (
              item,
              index
            ) => (

              <Col
                xl={3}
                md={6}
                key={index}
              >

                <Card>

                  <CardBody className="d-flex justify-content-between align-items-center">

                    <div>

                      <p className="text-uppercase text-muted fw-semibold mb-1">
                        {item.label}
                      </p>

                      <h3 className="mb-0">

                        <CountUp
                          start={0}
                          end={
                            item.value
                          }
                          duration={1.5}
                        />

                      </h3>

                    </div>


                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width:
                          "50px",
                        height:
                          "50px",
                        borderRadius:
                          "10px",
                        backgroundColor:
                          item.bgColor,
                      }}
                    >

                      <i
                        className={
                          item.icon
                        }
                        style={{
                          color:
                            item.color,
                          fontSize:
                            "24px",
                        }}
                      />

                    </div>

                  </CardBody>

                </Card>

              </Col>

            )
          )}

        </Row>


        {/* =================================================
            CREATE / UPDATE + MATRIX
        ================================================= */}

        <Row>

          {/* FORM */}

          <Col lg={4}>

            <Card>

              <CardHeader className="d-flex justify-content-between align-items-center">

                <h4 className="mb-0">

                  {editingId
                    ? "Update Mapping"
                    : "Create Mapping"}

                </h4>


                {editingId && (

                  <Button
                    color="light"
                    size="sm"
                    onClick={
                      resetForm
                    }
                  >
                    Cancel
                  </Button>

                )}

              </CardHeader>


              <CardBody>

                {/* CHANNEL */}

                <div className="mb-3">

                  <Label>
                    Channel Manager
                  </Label>

                  <Input
                    type="select"
                    name="channelManager"
                    value={
                      form.channelManager
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      loading
                    }
                  >

                    {CHANNEL_OPTIONS.map(
                      (
                        channel
                      ) => (

                        <option
                          key={
                            channel.value
                          }
                          value={
                            channel.value
                          }
                        >
                          {
                            channel.label
                          }
                        </option>

                      )
                    )}

                  </Input>

                </div>


                {/* PMS ROOM TYPE */}

                <div className="mb-3">

                  <Label>
                    PMS Room Type
                  </Label>

                  <Input
                    type="select"
                    name="pmsRoomTypeId"
                    value={
                      form.pmsRoomTypeId
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      loading
                    }
                  >

                    <option value="">
                      Select Room Type
                    </option>

                    {roomTypes.map(
                      (
                        room
                      ) => (

                        <option
                          key={
                            room._id
                          }
                          value={
                            room._id
                          }
                        >
                          {
                            getRoomTypeName(
                              room
                            )
                          }
                        </option>

                      )
                    )}

                  </Input>

                </div>


                {/* RATE PLAN */}

                <div className="mb-3">

                  <Label>
                    PMS Rate Plan
                  </Label>

                  <Input
                    type="text"
                    name="pmsRatePlanName"
                    value={
                      form.pmsRatePlanName
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      loading
                    }
                    placeholder="BAR"
                  />

                  <small className="text-muted">
                    Example: BAR, CP, MAP,
                    AP
                  </small>

                </div>


                {/* CHANNEL ROOM CODE */}

                <div className="mb-3">

                  <Label>
                    Channel Room Code
                  </Label>

                  <Input
                    type="text"
                    name="channelRoomCode"
                    value={
                      form.channelRoomCode
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      loading
                    }
                    placeholder="Example: 123456"
                  />

                  <small className="text-muted">
                    Room type ID/code provided
                    by the channel manager.
                  </small>

                </div>


                {/* CHANNEL RATE CODE */}

                <div className="mb-4">

                  <Label>
                    Channel Rate Code
                  </Label>

                  <Input
                    type="text"
                    name="channelRateCode"
                    value={
                      form.channelRateCode
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      loading
                    }
                    placeholder="Example: 789012"
                  />

                  <small className="text-muted">
                    Rate plan ID/code provided
                    by the channel manager.
                  </small>

                </div>


                {/* SELECTED ROOM PREVIEW */}

                {selectedRoomType && (

                  <div className="alert alert-light border mb-4">

                    <div className="fw-semibold">
                      PMS Room Type
                    </div>

                    <div className="text-muted">
                      {
                        getRoomTypeName(
                          selectedRoomType
                        )
                      }
                    </div>

                  </div>

                )}


                {/* SAVE */}

                <Button
                  color="success"
                  className="w-100"
                  onClick={
                    handleSave
                  }
                  disabled={
                    loading
                  }
                >

                  {loading ? (
                    <>
                      <Spinner
                        size="sm"
                        className="me-2"
                      />

                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-save me-1" />

                      {editingId
                        ? "Update Mapping"
                        : "Save Mapping"}
                    </>
                  )}

                </Button>


                {editingId && (

                  <Button
                    color="light"
                    className="w-100 mt-2"
                    onClick={
                      resetForm
                    }
                    disabled={
                      loading
                    }
                  >
                    Cancel
                  </Button>

                )}

              </CardBody>

            </Card>

          </Col>


          {/* MATRIX */}

          <Col lg={8}>

            <Card>

              <CardHeader className="d-flex justify-content-between align-items-center">

                <h4 className="mb-0">
                  Mapping Matrix
                </h4>

                <Badge color="success">
                  {mappings.length} Mappings
                </Badge>

              </CardHeader>


              <CardBody>

                {mappings.length === 0 ? (

                  <div className="text-center py-5">

                    <i
                      className="bx bx-link-alt text-muted"
                      style={{
                        fontSize:
                          "48px",
                      }}
                    />

                    <h5 className="mt-3">
                      No Room Mappings
                    </h5>

                    <p className="text-muted mb-0">
                      Create your first channel
                      manager mapping using the
                      form.
                    </p>

                  </div>

                ) : (

                  <TableContainer
                    columns={
                      mappingColumns
                    }
                    data={
                      mappings
                    }
                    customPageSize={
                      10
                    }
                    isPagination={
                      false
                    }
                    className="text-nowrap"
                    theadClass="table-light"
                  />

                )}

              </CardBody>

            </Card>

          </Col>

        </Row>


        {/* =================================================
            HISTORY
        ================================================= */}

        <Row>

          <Col lg={12}>

            <Card>

              <CardHeader className="d-flex justify-content-between align-items-center">

                <h4 className="mb-0">
                  Mapping History
                </h4>

                <Badge color="secondary">
                  Recent Updates
                </Badge>

              </CardHeader>


              <CardBody>

                {logsLoading ? (

                  <div className="text-center py-4">

                    <Spinner
                      size="sm"
                      color="primary"
                    />

                    <span className="ms-2 text-muted">
                      Loading history...
                    </span>

                  </div>

                ) : mappingLogs.length === 0 ? (

                  <div className="text-center text-muted py-4">
                    No mapping history found.
                  </div>

                ) : (

                  <TableContainer
                    columns={
                      logColumns
                    }
                    data={
                      mappingLogs
                    }
                    customPageSize={
                      10
                    }
                    isPagination={
                      false
                    }
                    className="text-nowrap"
                    theadClass="table-light"
                  />

                )}

              </CardBody>

            </Card>

          </Col>

        </Row>

      </Container>

    </div>
  );
};


export default RoomMapping;